import { Box, Grid, Typography } from '@mui/material'
import QuickLinks from 'components/layouts/AuthorizedLayout/component/QuickLinks'
import { USER_TOKEN } from 'config'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useLocalStorage } from 'utils'
import {
  type BillingData,
  type IPast12MonthsBillData,
  type IPast12MonthsUsageData,
  type UtilityType
} from 'utils/hooks/types'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import {
  Bill,
  CustomBox,
  NumberStats,
  PaperlessBilling,
  PowerCompare,
  PowerUsage
} from './components'

export const Dashboard: React.FC = () => {
  const {
    getUsage,
    services,
    account,
    currentAccount,
    premiseId,
    isAdmin,
    userEmail,
    getBilling,
    getLatestBill
  } = useAuth()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const isMobile = useMobileBreakpoints()
  const [past12MonthsUsageData, setPast12MonthsUsageData] = useState<
    IPast12MonthsUsageData[]
  >([])
  const [past12MonthsBillData, setPast12MonthsBillData] = useState<
    IPast12MonthsBillData[]
  >([])
  const [isUsageDataReady, setIsUsageDataReady] = useState<boolean>(false)
  const [latestBill, setLatestBill] = useState<BillingData[] | null>(null)
  const [isLatestBillReady, setIsLatestBillReady] = useState<boolean>(false)

  const initPast12MonthsUsageData = async (): Promise<void> => {
    setIsUsageDataReady(false)
    const getUsagePromises = services.map(async (service: UtilityType) => {
      const result = await getUsage({
        AccessToken: authToken,
        premiseId: currentAccount?.premiseId ?? premiseId,
        accountId: currentAccount?.accountId ?? account?.myAccount?.accountId,
        frequency: 'M',
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        service,
        admin: isAdmin,
        email: userEmail
      })

      return { service, usage: result?.usage }
    })

    const result = await Promise.all(getUsagePromises)
    setPast12MonthsUsageData(result)
    setIsUsageDataReady(true)
  }

  const initPast12MonthsBillData = async (): Promise<void> => {
    const getBillingPromises = services.map(async (service: UtilityType) => {
      const result = await getBilling({
        AccessToken: authToken,
        accountId: currentAccount?.accountId ?? account?.myAccount.accountId,
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        admin: isAdmin,
        email: userEmail,
        service
      })

      return { service, billing: result }
    })

    const result = await Promise.all(getBillingPromises)
    setPast12MonthsBillData(result)
  }

  const initGetLatestBill = async (): Promise<void> => {
    setIsLatestBillReady(false)
    const result = await getLatestBill({
      AccessToken: authToken,
      accountId: currentAccount?.accountId ?? account?.myAccount.accountId,
      admin: isAdmin,
      email: userEmail
    })
    setLatestBill(result?.billing)
    setIsLatestBillReady(true)
  }

  useEffect(() => {
    void initPast12MonthsUsageData()
    void initPast12MonthsBillData()
    void initGetLatestBill()
  }, [currentAccount])


  return (
    <>
      {isMobile && (
        <Box pb={2}>
          <Typography fontSize={24} fontWeight={300}>
            Welcome back <b>{account?.myAccount?.personName}</b>
          </Typography>
          <QuickLinks />
        </Box>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} xl={7}>
          <Bill
            latestBill={latestBill}
            past12MonthsUsageData={past12MonthsUsageData}
            isLoading={!isLatestBillReady}
          />
        </Grid>
        <Grid item xs={12} xl={5}>
          <NumberStats
            past12MonthsUsageData={past12MonthsUsageData}
            isLoading={!isUsageDataReady}
          />
        </Grid>
        <Grid item xs={12} xl={7}>
          <PowerUsage
            past12MonthsUsageData={past12MonthsUsageData}
            past12MonthsBillData={past12MonthsBillData}
            isLoading={!isUsageDataReady}
          />
        </Grid>
        <Grid item xs={12} xl={5}>
          <PaperlessBilling />
        </Grid>
        <Grid item xs={12} xl={7}>
          <PowerCompare
            past12MonthsUsageData={past12MonthsUsageData}
            isLoading={!isUsageDataReady}
          />
        </Grid>
        <Grid item xs={12} xl={5}>
          <CustomBox />
        </Grid>
      </Grid>
    </>
  )
}
